/* eslint-disable react/function-component-definition */
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import history from './history'
import Router from './Router'
import PermissionDenied from '../routes/Unauthorized/components/PermissionDenied'
import LoginContainer from '../routes/Login/containers/LoginContainer'
import loginReducer, {
  actions as loginActions,
  onFetchSyncStudyConfig,
} from '../routes/Login/modules/Login'
import ConsentContainer from '../routes/Consent/containers/ConsentContainer'
import consentReducer, {
  onFetchCompletedConsent,
  onFetchConsent,
} from '../routes/Consent/modules/Consent'
import SelectPTPContainer from '../routes/SelectPTP/containers/SelectPTPContainer'
import { caregiverActions } from '../store/selectedParticipant'
import ForgotPasswordContainer from '../routes/ForgotPassword/containers/ForgotPasswordContainer'
import restartParticipantReducer, {
  actions as restartParticipantActions,
} from '../routes/ForgotPassword/modules/restartParticipant'
import ChangePasswordPageContainer from '../routes/Profile/routes/ChangePassword/containers/ChangePasswordPageContainer'
import passwordReducer from '../routes/Profile/routes/ChangePassword/modules/Password'
import CoreLayout from '../layouts/CoreLayout/CoreLayout'
import {
  UserIsAuthenticatedAndHasConsented,
  UserIsAuthenticated,
  UserIsAuthenticatedAndIgnoreConsent,
  UserHasScope,
  UserIsNotAuthenticated,
} from '../containers/authContainers'
import { injectReducer, injectReducers } from '../store/reducers'
import noticeActions from '../store/notice'
import { setSubroute } from '../store/navbar'
import ActivitiesPageContainer from '../routes/Activities/containers/ActivitiesPageContainer'
import AnnouncementsPageContainer from '../routes/Announcements/containers/AnnouncementsPageContainer'
import VisitsPageContainer from '../routes/Visits/containers/VisitsPageContainer'
import WebSurveyContainer from '../routes/WebSurveys/containers/WebSurveyContainer'
import ProfilePageContainer from '../routes/Profile/routes/ProfilePage/containers/ProfilePageContainer'
import UsersDashboardContainer from '../routes/Users/routes/Dashboard/containers/UserContainer'
import UsersFormContainer from '../routes/Users/routes/CreateUser/containers/UserContainer'
import ActivateUserContainer from '../routes/Users/routes/ActivateUser/containers/ActivateUserContainer'
import ResetPasswordContainer from '../routes/Users/routes/ResetPassword/containers/ResetPasswordContainer'
import instrumentsReducer, {
  onFetchInstrumentsByArchitecture,
} from '../routes/Activities/modules/Instruments'
import webSurveyReducer, {
  onFetchWebSurvey,
} from '../routes/WebSurveys/modules/WebSurvey'
import announcementsReducer from '../routes/Announcements/modules/Announcements'
import visitsReducer, {
  onFetchSSUpcomingVisits,
} from '../routes/Visits/modules/Visits'
import usersReducer, {
  actions as usersActions,
} from '../routes/Users/routes/Dashboard/modules/Users'
import createUserReducer, {
  actions as createUserActions,
} from '../routes/Users/routes/CreateUser/modules/CreateUser'
import activateUserReducer, {
  actions as activateUserActions,
} from '../routes/Users/routes/ActivateUser/modules/ActivateUser'
import fileUploadReducer, {
  fetchFiles,
} from '../routes/Profile/routes/ProfilePage/modules/FileUpload'
import Loader from '../components/D3Loader'
import localizeApp from 'utils/localization'
import { toControlSurveyUrl } from '../routes/WebSurveys/utils/webSurveyUrl'

const UnauthorizedPage = UserIsAuthenticatedAndHasConsented(PermissionDenied)
const LoginPage = UserIsNotAuthenticated(LoginContainer)
const ConsentPage = UserIsAuthenticated(ConsentContainer)
const SelectPTPPage = UserIsAuthenticatedAndHasConsented(SelectPTPContainer)
const ChangePasswordPage = UserIsAuthenticated(ChangePasswordPageContainer)
// Use UserIsAuthenticatedAndHasConsented so blocking consent and change-password truly block
// access to all app views; user is redirected to /consent or /profile/change-password until complete.
const ActivitiesPage = UserIsAuthenticatedAndHasConsented(
  ActivitiesPageContainer,
)
const InboxPage = UserIsAuthenticatedAndHasConsented(AnnouncementsPageContainer)
const VisitsPage = UserIsAuthenticatedAndHasConsented(VisitsPageContainer)
const WebSurveyPage = UserIsAuthenticatedAndHasConsented(WebSurveyContainer)
const ProfilePage = UserIsAuthenticatedAndHasConsented(ProfilePageContainer)
const UsersDashboardPage = UserIsAuthenticated(
  UserHasScope(['ru'], UsersDashboardContainer),
)
const UsersCreatePage = UserIsAuthenticated(
  UserHasScope(['wu'], UsersFormContainer),
)
const UsersMePage = UserIsAuthenticated(UsersFormContainer)
const UsersViewPage = UserIsAuthenticated(
  UserHasScope(['ru'], UsersFormContainer),
)
const { LANDING_MESSAGE_ENABLED, IS_POSITIVE_MESSAGE } = process.env
const storePropType = PropTypes.shape({
  dispatch: PropTypes.func.isRequired,
  getState: PropTypes.func.isRequired,
})

const toLegacyLocation = (location) => ({
  pathname: location.pathname,
  search: location.search,
  query: Object.fromEntries(new URLSearchParams(location.search || '')),
})

const useRouteProps = () => {
  const location = useLocation()
  const params = useParams()
  const routeProps = useMemo(
    () => ({ location: toLegacyLocation(location), params }),
    [location, params],
  )
  return { location, params, routeProps }
}

const RouteLoadError = ({ error }) => (
  <div style={{ padding: 20, fontFamily: 'monospace', color: '#c00' }}>
    <h2>Route Load Error</h2>
    <pre>{error?.message ?? String(error)}</pre>
  </div>
)

/* eslint-disable react-hooks/exhaustive-deps */
const useAsyncSetup = (setup, deps = []) => {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    setReady(false)
    setError(null)

    Promise.resolve()
      .then(setup)
      .then(() => {
        if (active) setReady(true)
      })
      .catch((err) => {
        if (active) setError(err)
      })

    return () => {
      active = false
    }
  }, deps)

  return { ready, error }
}
/* eslint-enable react-hooks/exhaustive-deps */

const RR6ActivitiesRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    injectReducers(store, {
      instruments: instrumentsReducer,
      webSurveys: webSurveyReducer,
      announcements: announcementsReducer,
    })
    store.dispatch(setSubroute('activities'))
    const state = store.getState()
    const pathname = state?.location?.pathname || ''
    const user = state?.user || {}
    const emptyUser = Object.keys(user).length === 0

    if (!pathname.includes('/login') && !emptyUser) {
      store.dispatch(onFetchInstrumentsByArchitecture())
      store.dispatch(onFetchConsent())
      if (!user?.latest_app_version) {
        store.dispatch(onFetchSyncStudyConfig())
      }
    }
  }, [store, routeProps.location.pathname])

  return (
    <CoreLayout location={routeProps.location}>
      <ActivitiesPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6InboxRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducers(store, {
    announcements: announcementsReducer,
    instruments: instrumentsReducer,
  })

  useEffect(() => {
    store.dispatch(setSubroute('announcements'))
    const state = store.getState()
    const pathname = state?.location?.pathname || ''
    const user = state?.user || {}
    if (!pathname.includes('/login')) {
      store.dispatch(onFetchInstrumentsByArchitecture())
      if (!user?.latest_app_version) {
        store.dispatch(onFetchSyncStudyConfig())
      }
    }
  }, [store, routeProps.location.pathname])

  return (
    <CoreLayout location={routeProps.location}>
      <InboxPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6WebSurveyRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducers(store, {
    webSurveys: webSurveyReducer,
    instruments: instrumentsReducer,
  })

  const surveyKey = `${routeProps.location.pathname}${routeProps.location.search || ''}`
  const loadedSurveyKeyRef = useRef(null)
  const [readyKey, setReadyKey] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    setError(null)

    // GOLD-12345: same survey URL must not refetch/unmount while the
    // participant is already answering. A late setWebSurvey can inject
    // resume-review and snap the page back to the start.
    if (loadedSurveyKeyRef.current === surveyKey) {
      return
    }

    setReadyKey(null)

    const { pathname, search = '' } = routeProps.location
    const queryUrl = toControlSurveyUrl(pathname, search)

    Promise.resolve(store.dispatch(onFetchInstrumentsByArchitecture(true)))
      .then(() => store.dispatch(onFetchWebSurvey(queryUrl)))
      .then(() => {
        if (!active) return
        store.dispatch(setSubroute('web-survey'))
        loadedSurveyKeyRef.current = surveyKey
        setReadyKey(surveyKey)
      })
      .catch((err) => {
        if (active) setError(err)
      })

    return () => {
      active = false
    }
  }, [
    store,
    surveyKey,
    routeProps.location.pathname,
    routeProps.location.search,
  ])

  if (error) return <RouteLoadError error={error} />
  return (
    <CoreLayout location={routeProps.location}>
      {readyKey !== surveyKey ? <Loader /> : <WebSurveyPage {...routeProps} />}
    </CoreLayout>
  )
}

const RR6VisitsRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  // Must not call injectReducers during render — replaceReducer notifies subscribers
  // and triggers "Cannot update Connect(CoreLayout) while rendering RR6VisitsRoute".
  // useLayoutEffect runs after commit; VisitsPageContainer tolerates missing visits on first paint via ?? [].
  useLayoutEffect(() => {
    injectReducers(store, {
      instruments: instrumentsReducer,
      webSurveys: webSurveyReducer,
      announcements: announcementsReducer,
      visits: visitsReducer,
    })
  }, [store])

  useEffect(() => {
    store.dispatch(setSubroute('visits'))
    const state = store.getState()
    const pathname = state?.location?.pathname || ''
    const user = state?.user || {}
    const emptyUser = Object.keys(user).length === 0
    if (!pathname.includes('/login') && !emptyUser) {
      store.dispatch(onFetchInstrumentsByArchitecture())
      store.dispatch(onFetchSSUpcomingVisits(user?.study_id))
      store.dispatch(onFetchConsent())
      if (!user?.latest_app_version) {
        store.dispatch(onFetchSyncStudyConfig())
      }
    }
  }, [store, routeProps.location.pathname])

  return (
    <CoreLayout location={routeProps.location}>
      <VisitsPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6ProfileIndexRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    injectReducers(store, {
      announcements: announcementsReducer,
      files: fileUploadReducer,
      consent: consentReducer,
    })
    store.dispatch(setSubroute('profile'))
    store.dispatch(onFetchCompletedConsent())
    store.dispatch(fetchFiles())

    const state = store.getState()
    const pathname = state?.location?.pathname || ''
    const user = state?.user || {}
    if (
      !pathname.includes('/login') &&
      !pathname.includes('/change-password')
    ) {
      store.dispatch(onFetchConsent())
      if (!user?.latest_app_version) {
        store.dispatch(onFetchSyncStudyConfig())
      }
    }
  }, [store, routeProps.location.pathname])

  return (
    <CoreLayout location={routeProps.location}>
      <ProfilePage {...routeProps} />
    </CoreLayout>
  )
}

const RR6UsersIndexRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducer(store, { key: 'users', reducer: usersReducer })

  useEffect(() => {
    store.dispatch(setSubroute('users'))
    store.dispatch(usersActions.fetchUsers(store.getState().user.id))
  }, [store])

  return (
    <CoreLayout location={routeProps.location}>
      <UsersDashboardPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6UsersActivateRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducer(store, { key: 'activateUser', reducer: activateUserReducer })

  const { ready, error } = useAsyncSetup(() => {
    const token = new URLSearchParams(routeProps.location.search || '').get(
      'token',
    )
    return store.dispatch(activateUserActions.activateUser(token))
  }, [store, routeProps.location.search])

  if (error) return <RouteLoadError error={error} />
  if (!ready) return null
  return (
    <CoreLayout location={routeProps.location}>
      <ActivateUserContainer {...routeProps} />
    </CoreLayout>
  )
}

const RR6UsersResetRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducer(store, { key: 'resetPassword', reducer: activateUserReducer })

  const { ready, error } = useAsyncSetup(() => {
    const token = new URLSearchParams(routeProps.location.search || '').get(
      'token',
    )
    return store.dispatch(activateUserActions.activateUser(token, true))
  }, [store, routeProps.location.search])

  if (error) return <RouteLoadError error={error} />
  if (!ready) return null
  return (
    <CoreLayout location={routeProps.location}>
      <ResetPasswordContainer {...routeProps} />
    </CoreLayout>
  )
}

const RR6UsersCreateRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducer(store, { key: 'userReducer', reducer: createUserReducer })

  useEffect(() => {
    store.dispatch(setSubroute('user'))
    store.dispatch(usersActions.fetchUsers(store.getState().user.id))
    store.dispatch(createUserActions.resetUserPage())
  }, [store])

  return (
    <CoreLayout location={routeProps.location}>
      <UsersCreatePage {...routeProps} />
    </CoreLayout>
  )
}

const RR6UsersMeRoute = ({ store }) => {
  const { routeProps } = useRouteProps()
  injectReducer(store, { key: 'userReducer', reducer: createUserReducer })

  useEffect(() => {
    store.dispatch(setSubroute('user'))
    store.dispatch(createUserActions.resetUserPage())
    store.dispatch(createUserActions.fetchUser('me'))
  }, [store])

  return (
    <CoreLayout location={routeProps.location}>
      <UsersMePage {...routeProps} />
    </CoreLayout>
  )
}

const RR6UsersViewRoute = ({ store }) => {
  const { routeProps, params } = useRouteProps()
  injectReducer(store, { key: 'userReducer', reducer: createUserReducer })

  useEffect(() => {
    store.dispatch(setSubroute('user'))
    store.dispatch(usersActions.fetchUsers(store.getState().user.id))
    store.dispatch(createUserActions.resetUserPage())
    store.dispatch(createUserActions.fetchUser(params?.id))
  }, [params?.id, store])

  return (
    <CoreLayout location={routeProps.location}>
      <UsersViewPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6LoginRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    const humiIsabellaDisabled = false
    injectReducer(store, { key: 'login', reducer: loginReducer })
    store.dispatch(loginActions.clearLoginError())
    if (LANDING_MESSAGE_ENABLED === 'YES') {
      store.dispatch(
        noticeActions.openNotice({
          className: 'notice',
          noButtons: true,
          closeOnBackgroundClick: true,
          humi: !humiIsabellaDisabled,
          happyHumi: IS_POSITIVE_MESSAGE === 'YES',
          closeX: true,
        }),
      )
    }
  }, [store])

  return (
    <CoreLayout location={routeProps.location}>
      <LoginPage />
    </CoreLayout>
  )
}

const RR6ConsentRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    injectReducers(store, { consent: consentReducer })
    store.dispatch(setSubroute('consent'))
    const state = store.getState()
    const studyID = state?.user?.study_id
    store.dispatch(onFetchConsent(studyID))
    store.dispatch(onFetchCompletedConsent())
  }, [store, routeProps.location.pathname])

  return (
    <CoreLayout location={routeProps.location}>
      <ConsentPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6SelectPTPRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    store.dispatch(setSubroute('select_ptp'))
    store.dispatch(caregiverActions.fetchParticipants())
  }, [store])

  return (
    <CoreLayout location={routeProps.location}>
      <SelectPTPPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6ForgotPasswordRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    injectReducer(store, {
      key: 'restartParticipant',
      reducer: restartParticipantReducer,
    })
    const token = new URLSearchParams(routeProps.location.search || '').get(
      'token',
    )
    store.dispatch(restartParticipantActions.restartParticipant(token))
  }, [routeProps.location.search, store])

  return (
    <CoreLayout location={routeProps.location}>
      <ForgotPasswordContainer {...routeProps} />
    </CoreLayout>
  )
}

const RR6ProfileChangePasswordRoute = ({ store }) => {
  const { routeProps } = useRouteProps()

  useEffect(() => {
    store.dispatch(setSubroute('password'))
    injectReducers(store, {
      password: passwordReducer,
      login: loginReducer,
    })
  }, [store])

  return (
    <CoreLayout location={routeProps.location}>
      <ChangePasswordPage {...routeProps} />
    </CoreLayout>
  )
}

const RR6UnauthorizedRoute = () => {
  const { routeProps } = useRouteProps()
  return (
    <CoreLayout location={routeProps.location}>
      <UnauthorizedPage />
    </CoreLayout>
  )
}

const RR6RouterApp = ({ store }) => {
  useEffect(() => {
    localizeApp({ dispatch: store.dispatch, getState: store.getState })
  }, [store])

  return (
    <Router history={history}>
      <Routes>
        <Route path='/' element={<Navigate replace to='/my-activities' />} />
        <Route path='/login' element={<RR6LoginRoute store={store} />} />
        <Route path='/unauthorized' element={<RR6UnauthorizedRoute />} />
        <Route path='/consent' element={<RR6ConsentRoute store={store} />} />
        <Route
          path='/my-activities'
          element={<RR6ActivitiesRoute store={store} />}
        />
        <Route path='/inbox' element={<RR6InboxRoute store={store} />} />
        <Route
          path='/web-survey/studies/:studyId/instruments/:instrumentId/:instrument'
          element={<RR6WebSurveyRoute store={store} />}
        />
        <Route path='/visits' element={<RR6VisitsRoute store={store} />} />
        <Route
          path='/forgot-password'
          element={<RR6ForgotPasswordRoute store={store} />}
        />
        <Route
          path='/select-ptp'
          element={<RR6SelectPTPRoute store={store} />}
        />
        <Route
          path='/profile'
          element={<RR6ProfileIndexRoute store={store} />}
        />
        <Route
          path='/profile/change-password'
          element={<RR6ProfileChangePasswordRoute store={store} />}
        />
        <Route path='/users' element={<RR6UsersIndexRoute store={store} />} />
        <Route
          path='/users/activate'
          element={<RR6UsersActivateRoute store={store} />}
        />
        <Route
          path='/users/reset'
          element={<RR6UsersResetRoute store={store} />}
        />
        <Route
          path='/users/create'
          element={<RR6UsersCreateRoute store={store} />}
        />
        <Route path='/users/me' element={<RR6UsersMeRoute store={store} />} />
        <Route
          path='/users/:id'
          element={<RR6UsersViewRoute store={store} />}
        />
        <Route path='*' element={<Navigate replace to='/my-activities' />} />
      </Routes>
    </Router>
  )
}

RR6LoginRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6ConsentRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6SelectPTPRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6ForgotPasswordRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6ProfileChangePasswordRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6ActivitiesRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6InboxRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6WebSurveyRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6VisitsRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6ProfileIndexRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6UsersIndexRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6UsersActivateRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6UsersResetRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6UsersCreateRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6UsersMeRoute.propTypes = {
  store: storePropType.isRequired,
}

RR6UsersViewRoute.propTypes = {
  store: storePropType.isRequired,
}

RouteLoadError.propTypes = {
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

RouteLoadError.defaultProps = {
  error: null,
}

RR6RouterApp.propTypes = {
  store: storePropType.isRequired,
}

export default RR6RouterApp
